import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api'
import { useGemerkt, useLaden, useScrollMerken } from '../hooks'
import KladdeTabelle from '../komponenten/KladdeTabelle'
import Meldung from '../komponenten/Meldung'
import { datum, plusTage } from '../format'
import type { Kladde, ReportStand } from '../typen'

export default function KladdeSeite() {
  // Gruppe und Zeitraum überstehen den Wechsel der Reiter (useGemerkt) - zurück auf diesem Reiter
  // steht man wieder auf demselben Blatt.
  const [gruppe, setGruppe] = useGemerkt<string>('kladde.gruppe', '')
  // Leer heißt: das Backend wählt den Zeitraum um die zuletzt gebuchten Tage. Ein Kalenderfenster
  // ab heute liefe ins Leere, solange die Kladde noch von Hand nachgezogen wird.
  const [von, setVon] = useGemerkt('kladde.von', '')
  const [bis, setBis] = useGemerkt('kladde.bis', '')
  // Der letzte gebuchte Tag - die Zeile über der Tabelle erklärt damit, warum der Zeitraum dort endet.
  const [letzteBuchung, setLetzteBuchung] = useState<string | null>(null)
  const [zeitraumVoreingestellt, setZeitraumVoreingestellt] = useGemerkt('kladde.voreingestellt', true)

  const { daten: gruppen, fehler: gruppenFehler } = useLaden(() => api.gruppen(), [], {
    onGeladen: (werte) => {
      if (werte.length > 0) setGruppe((alt) => (alt && werte.includes(alt) ? alt : werte[0]))
    },
  })
  // Wie alt die Kladde ist: die letzte gebuchte Ergebnismenge. Ohne sie steht hier nur, was von Hand kam.
  const { daten: reports } = useLaden<ReportStand[]>(() => api.importeLetzte(), [])
  const ergebnismenge = (reports ?? []).find((r) => r.dokumenttyp === 'ergebnismenge')
  const ergebnismengeFehlt = reports !== null && (ergebnismenge === undefined || ergebnismenge.tage_her === null || !ergebnismenge.gebucht_am)
  const ergebnismengeAlt = ergebnismengeFehlt || (ergebnismenge?.tage_her ?? 0) > 3

  const {
    daten: kladde,
    laedt,
    fehler: kladdeFehler,
  } = useLaden<Kladde>(() => api.kladde(gruppe, von, bis), [gruppe, von, bis], {
    wenn: Boolean(gruppe),
    onGeladen: (wert) => {
      if (wert.zeilen.length === 0) return
      const tage = wert.zeilen.map((z) => z.tag).sort()
      const letzter = tage[tage.length - 1]
      setLetzteBuchung((alt) => (alt === null || letzter > alt ? letzter : alt))
      // Beim ersten Laden den Zeitraum aus der Antwort übernehmen, damit die Datumsfelder
      // zeigen, was tatsächlich zu sehen ist.
      if (!bis) {
        // Die letzten 30 Tage vor dem letzten Eintrag - ein einzelner Tag sagt nichts.
        const anfang = plusTage(letzter, -30)
        setVon(anfang < tage[0] ? anfang : tage[0])
        setBis(letzter)
      }
    },
  })
  const fehler = gruppenFehler ?? kladdeFehler
  // Wo man in der Kladde stand, übersteht den Reiterwechsel wie im Dispositionsblatt (Frau Barth, Rückmeldung
  // 20.10.2026; Runde 4, 04.11.2026). Dafür bleibt beim Neuladen die alte Tabelle blass stehen, statt abgebaut
  // zu werden - sonst wird die Seite kurz und der Browser rollt nach oben.
  const rahmen = useRef<HTMLDivElement | null>(null)
  useScrollMerken('kladde.scroll', rahmen, Boolean(kladde) && !laedt)

  function zeitraumSetzen(setzer: (wert: string) => void, wert: string) {
    setzer(wert)
    setZeitraumVoreingestellt(false)
  }

  return (
    <>
      <div className="leiste">
        <label>
          Gruppe{' '}
          <select value={gruppe} onChange={(e) => setGruppe(e.target.value)}>
            {(gruppen ?? []).length === 0 && <option value="">keine vorhanden</option>}
            {(gruppen ?? []).map((wert) => (
              <option key={wert} value={wert}>
                {wert}
              </option>
            ))}
          </select>
        </label>
        <label>
          von <input type="date" value={von} onChange={(e) => zeitraumSetzen(setVon, e.target.value)} />
        </label>
        <label>
          bis <input type="date" value={bis} onChange={(e) => zeitraumSetzen(setBis, e.target.value)} />
        </label>
        <a className="knopf" href={api.exportKladde(gruppe, von, bis)} download>
          Als Excel (Kladde)
        </a>
        <span className="hinweis" title="Zellen lassen sich markieren und nach Excel kopieren - oder als Mappe im Layout der Excel-Kladde herunterladen.">
          Alle Werte in Tonnen Fertigprodukt.
        </span>
      </div>

      {/* Eine Zeile zum Stand: letzte Buchung, Alter der Ergebnismenge, und warum der Zeitraum so liegt. */}
      {(letzteBuchung || reports !== null) && (
        <p className={`stand-zeile${ergebnismengeAlt ? ' alt' : ''}`}>
          {letzteBuchung ? `Letzte Buchung ${datum(letzteBuchung)}` : 'Noch keine Buchung'}
          {zeitraumVoreingestellt && letzteBuchung && <span className="hinweis"> (Zeitraum: 30 Tage bis dahin)</span>}
          {reports !== null && (
            <>
              {' - '}
              {ergebnismengeFehlt ? (
                <>
                  seitdem keine Ergebnismenge: PDF an othuena@intsode.com schicken oder unter <Link to="/importe">Importe</Link> hochladen.
                </>
              ) : (
                <>
                  letzte Ergebnismenge {datum((ergebnismenge?.gebucht_am ?? '').slice(0, 10))} ({ergebnismenge?.tage_her} Tage her)
                  {ergebnismengeAlt && (
                    <>
                      : neues PDF an othuena@intsode.com schicken oder unter <Link to="/importe">Importe</Link> hochladen.
                    </>
                  )}
                </>
              )}
            </>
          )}
        </p>
      )}

      {fehler && (
        <Meldung art="fehler" titel="Die Kladde konnte nicht geladen werden">
          {fehler}
        </Meldung>
      )}
      {laedt && !kladde && <p className="hinweis">Wird geladen.</p>}
      {kladde && (
        <div className={`tabellen-huelle${laedt ? ' veraltet' : ''}`} aria-busy={laedt}>
          <KladdeTabelle kladde={kladde} rahmen={rahmen} />
        </div>
      )}
      {!laedt && !kladde && !fehler && (
        <p className="leer">Noch keine Gruppe gewählt.</p>
      )}
    </>
  )
}
